/* eslint-disable @typescript-eslint/no-misused-promises */
import React from 'react'
import { Box, Divider, Grid } from '@mui/material'
import { useForm, FormProvider } from 'react-hook-form-mui'
import GenericModal from '../../../../components/GenericModal'
import MultiSelectWithCheckBox from '../../../../components/FormElements/MultiSelectWithCheckBox'
import FormAutoComplete from '../../../../components/FormElements/FormAutoComplete'
import FormInput from '../../../../components/FormElements/FormInput'
import DefaultModalActions from '../../../../components/GenericModal/DefaultModalActions'
import { ButtonType } from '../../../../components/GenericButton/styles'
import { shade } from '../../../../colors'
import { BranchingOperation, type Question, SurveyItemQuestionType } from '../../types'
import { useAppDispatch } from '../../../../store'
import { addBranchSection, type ISurveySection, setBranchModal } from '../../../../store/slices/SurveyTemplateSlice'
import { getSelectedSection } from '../../../../store/slices/SurveyTemplateSlice/selectors'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { BranchingOperationsDropdown } from '../../constant'
import { SurveyQuestionType } from '../../../../models/Survey'
import { checkQuestionOptionHaveBranch, isOperationOptionDisabled } from '../../helpers'

// Validation schema
const validationSchema = yup.object().shape({
  operation: yup
    .object()
    .shape({
      value: yup.string().required('Operation is required')
    })
    .required('Operation is required'),

  answers: yup.array()
    .when('operation', {
      is: (operation: any) => operation.value === BranchingOperation.Is,
      then: schema => schema.min(1, 'Answers are required when operation is "Is"'),
      otherwise: schema => schema.notRequired()
    })
})

interface BranchModalProps {
  questionData: Question
  questionIndex: number
  handleClickAway?: () => void
  section: ISurveySection
  isOpen: boolean
}

const BranchModal: React.FC<BranchModalProps> = ({
  questionData,
  questionIndex,
  handleClickAway,
  section,
  isOpen
}) => {
  const dispatch = useAppDispatch()
  const selectedSection = getSelectedSection()

  const methods = useForm<any>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      operation: '',
      answers: []
    }
  })

  const { handleSubmit, watch, formState } = methods

  const handleCloseModal = (): void => {
    dispatch(setBranchModal(false))
  }

  const onSubmit = (values: any): void => {
    const payload = {
      parentSurveySectionId: selectedSection.surveySectionId,
      indentation: selectedSection.indentation,
      branchingFilters: {
        operation: values.operation?.value,
        itemId: questionData?.itemId,
        itemType: questionData?.itemType,
        prompt: questionData?.prompt,
        answer: questionData?.itemType === SurveyItemQuestionType.LINEAR_SCALE
          ? values.answers.map((a: any) => a.option)
          : values.answers
      },
      section
    }
    handleClickAway?.()
    dispatch(addBranchSection(payload))
    handleCloseModal()
  }

  const getOptionLabel = (option: any): string => {
    return questionData?.itemType === SurveyItemQuestionType.LINEAR_SCALE
      ? option.option
      : option
  }

  const getOptionValue = (option: any): string => {
    return questionData?.itemType === SurveyItemQuestionType.LINEAR_SCALE
      ? option?.option
      : option
  }

  const operation = watch('operation').value
  const isAnswersDropdownDisabled = operation !== BranchingOperation.Is || questionData?.itemType === SurveyQuestionType.FreeResponse
  const branchingOperationsDropdown = questionData?.itemType === SurveyItemQuestionType.FREE_RESPONSE ? BranchingOperationsDropdown.filter(operation => operation.value !== BranchingOperation.Is) : BranchingOperationsDropdown

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={handleCloseModal}
      title="Survey Branching"
      showCloseIcon={false}
      maxWidth="md"
      action={false}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box
            mt={4}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            border={`1px solid ${shade[650]}`}
            padding="24px 16px"
            borderRadius="8px"
            bgcolor={shade[750]}
          >
            <Box display="flex" flexDirection="column" gap={2}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={5} md={4.5}>
                  <FormInput
                    defaultValue={selectedSection?.name ?? ''}
                    placeholder="Section title"
                    name="section"
                    disabled
                  />
                </Grid>

                <Grid item xs={12} sm={3} md={4.5}>
                  <FormInput
                    defaultValue={questionData?.prompt ?? ''}
                    placeholder="Question prompt"
                    name="question"
                    disabled
                  />
                </Grid>

                <Grid item xs={12} sm={3} md={3}>
                  <FormAutoComplete
                    options={branchingOperationsDropdown}
                    placeholder="Operations"
                    name="operation"
                    getOptionLabel={(option) => option?.title || ''}
                    getOptionValue={(option) => option || ''}
                    getOptionDisabled={(operation) => isOperationOptionDisabled(operation, questionData)}
                    required
                    sx={{
                      bgcolor: '#fff'
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={3} md={5}>
                  <MultiSelectWithCheckBox
                    name="answers"
                    options={questionData?.object as any ?? []}
                    placeholder="Answers"
                    getOptionLabel={getOptionLabel}
                    getOptionValue={getOptionValue}
                    fullWidth
                    required
                    disabled={isAnswersDropdownDisabled}
                    getOptionDisabled={(option) => checkQuestionOptionHaveBranch(option, questionData)}
                    sx={{
                      bgcolor: '#fff'
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>
          <Divider sx={{ my: 8, ml: '-20px', mr: '-20px' }} />
          <DefaultModalActions
            confirmButtonProps={{
              type: 'submit',
              text: 'Save'
            }}
            destructiveButtonProps={{
              buttonType: ButtonType.Secondary,
              onClick: handleCloseModal
            }}
          />
        </form>
      </FormProvider>
    </GenericModal>
  )
}

export default BranchModal
